import { useNavigate, useSearchParams } from "react-router-dom";
import { usePageBar } from "../contexts/PageBarContext";
import { collection, serverTimestamp, Timestamp } from "firebase/firestore";
import { createLocalDoc } from "../utils/offlineWrites";
import { linkInteractionsToEntity } from "../utils/entityLinking";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { EntityForm } from "../components/entities/EntityForm";
import { normalizeEntityType, type CustomQuestion, type EventOption } from "../types";

/**
 * Only in-app paths, so a crafted `returnTo` can't bounce the user off the app.
 */
function safeReturnTo(value: string | null): string | null {
  return value && value.startsWith("/") && !value.startsWith("//") ? value : null;
}

export function EntityCreate() {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [searchParams] = useSearchParams();
  const canManage = userProfile?.roles?.includes("manage_entities");
  const cancelTo = safeReturnTo(searchParams.get("returnTo")) ?? "/entities";
  usePageBar({ title: "Add entity", cancel: { onClick: () => navigate(cancelTo) } });

  if (!canManage) {
    return (
      <div className="page">
        <h1>Add Entity</h1>
        <p className="text-muted">You don't have permission to manage entities.</p>
      </div>
    );
  }

  const presetParentId = searchParams.get("parentId");
  const presetTypeParam = searchParams.get("type");
  const presetType = normalizeEntityType(presetTypeParam);
  // Promoting activities that were logged as free text: the name they were
  // logged under seeds the form, and once the place exists they're pointed at
  // it and the user lands back where they started.
  const presetName = searchParams.get("name")?.trim() || null;
  const linkInteractionIds = (searchParams.get("linkInteractions") ?? "")
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);
  const returnTo = safeReturnTo(searchParams.get("returnTo"));

  const handleSubmit = async (data: {
    displayName: string;
    type: string;
    parentId: string | null;
    imageUrl: string;
    externalId: string;
    externalSource: string;
    activeFrom: string;
    activeTo: string;
    timeInputMode: string | null;
    eventOptions: EventOption[];
    customQuestions: CustomQuestion[];
  }) => {
    const entityId = createLocalDoc(collection(db, "entities"), {
      displayName: data.displayName,
      type: data.type,
      parentId: data.parentId,
      imageUrl: data.imageUrl || null,
      externalId: data.externalId || null,
      externalSource: data.externalSource || null,
      activeFrom: data.activeFrom ? Timestamp.fromDate(new Date(data.activeFrom + "T00:00:00")) : null,
      activeTo: data.activeTo ? Timestamp.fromDate(new Date(data.activeTo + "T00:00:00")) : null,
      customConfig: {
        ...(data.timeInputMode ? { time_input_mode: data.timeInputMode } : {}),
        ...(data.customQuestions.length > 0 ? { custom_questions: data.customQuestions } : {}),
      },
      activityOptions: [],
      eventOptions: data.eventOptions,
      location: null,
      createdAt: Timestamp.now(),
      updatedAt: serverTimestamp(),
    });
    if (linkInteractionIds.length > 0) {
      linkInteractionsToEntity(linkInteractionIds, entityId);
    }
    navigate(returnTo ?? `/entities/${entityId}`);
  };

  const initial =
    presetParentId || presetType || presetName
      ? {
          parentId: presetParentId,
          ...(presetType ? { type: presetType } : {}),
          ...(presetName ? { displayName: presetName } : {}),
        }
      : undefined;

  return (
    <div className="page">
      <h1>Add Entity</h1>
      {linkInteractionIds.length > 0 && (
        <p className="text-muted">
          {linkInteractionIds.length === 1
            ? "The activity you came from will be linked to this place once it's created."
            : `${linkInteractionIds.length} activities will be linked to this place once it's created.`}
        </p>
      )}
      <EntityForm
        initial={initial}
        onSubmit={handleSubmit}
        onCancel={() => navigate(returnTo ?? "/entities")}
      />
    </div>
  );
}
